<template>
  <dialog ref="dialogRef" class="rwr-dialog" @close="emit('close')" @click="handleBackdropClick">
    <div class="rwr-card">
      <div class="rwr-title">{{ localize('walk.walk_reds.result_title') }}</div>
      <div class="rwr-value">{{ props.corrected }}/{{ props.total }}</div>
      <div v-if="props.dots.length" class="rwr-dots">
        <span v-for="(solved, i) in props.dots" :key="i" class="rwr-dot" :class="solved ? 'correct' : 'wrong'" />
      </div>
      <div class="rwr-actions">
        <span v-if="mapEnabled" class="rwr-view-map" @click="handleViewOnMap" @touchend.prevent="handleViewOnMap">{{
          localize('walk.reveal.view_on_map')
        }}</span>
      </div>
      <div class="rwr-close" @click="emit('close')" @touchend.prevent="emit('close')">
        <GamIcon :name="GamIconName.X_CLOSE" size="24px" is-current />
      </div>
    </div>
  </dialog>
</template>

<script lang="ts" setup>
import { localize } from '@/core/gambits';
import { platform } from '@/core/platform';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps<{
  corrected: number;
  total: number;
  dots: boolean[];
}>();

const emit = defineEmits<{ close: []; 'view-map': [] }>();

const dialogRef = ref<HTMLDialogElement>();
const mapEnabled = computed(() => !(platform.isOfflineCapable && !navigator.onLine));

onMounted(() => {
  dialogRef.value?.showModal();
});

onBeforeUnmount(() => {
  if (dialogRef.value?.open) dialogRef.value.close();
});

const handleBackdropClick = (e: MouseEvent) => {
  if (e.target === dialogRef.value) emit('close');
};

function handleViewOnMap() {
  emit('view-map');
}
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.rwr-dialog {
  width: 100%;
  height: 100%;
  border: 0;
  // The dialog fills a viewport that runs under the notch and the home
  // indicator, so the card is centred inside the safe area instead.
  padding: var(--sai-top) 0 var(--sai-bottom);
  margin: 0;
  max-width: 100%;
  max-height: 100%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  overflow: hidden;

  &::backdrop {
    background: rgba(23, 23, 23, 0.75);
  }
}

.rwr-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--spacing-xxl) var(--spacing-xl);
  padding-bottom: 5rem;
  background: var(--color-dark-700);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-large);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  max-width: 300px;
  width: 100%;
}

.rwr-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-white);
  margin-bottom: var(--spacing-md);
}

.rwr-value {
  font-size: 36px;
  font-weight: 800;
  color: var(--color-incorrect);
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.rwr-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: var(--spacing-md);
  flex-wrap: wrap;
}

.rwr-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;

  &.correct {
    background: oklch(0.55 0.15 145);
  }

  &.wrong {
    background: var(--color-incorrect);
  }
}

.rwr-actions {
  width: 100%;
  margin-top: var(--spacing-l);
}

.rwr-view-map {
  width: 100%;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-white-70);
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  padding: 0 20px;
  height: 3rem;
  border-radius: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }
}

.rwr-close {
  position: absolute;
  bottom: 0.75rem;
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: rgba(0, 0, 0, 0.2);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
  transition: all 200ms linear;

  @include hover {
    color: var(--color-white);
    border-color: var(--color-white-20);
  }

  @include press($transform: translateX(-50%));
}
</style>
